import React, { useCallback, useMemo, useRef } from 'react'
import { ChartType, chartTypeFromJSON } from '../../proto/generated/chart'
import Bar from '../../assets/icons/chartTypes/bars'
import Candle from '../../assets/icons/chartTypes/candles'
import HollowCandle from '../../assets/icons/chartTypes/hollowCandles'
import HighLowCandle from '../../assets/icons/chartTypes/highLowCandles'
import Area from '../../assets/icons/chartTypes/area'
import Histogram from '../../assets/icons/chartTypes/histogram'
import Line from '../../assets/icons/chartTypes/line'
import Baseline from '../../assets/icons/chartTypes/baseline'
import { SuperChartConfiguration_ChartType } from '../../proto/generated/superchart-page'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, useTheme } from '@mui/material'
import { updateChartType, updateScreenerChartType } from '../../reducer/chart.reducer'
import { selectChartType, selectChartTypes, selectScreenerChartType } from '../../selectors/chart'
import { ExtraSmallButton } from '../custom'
import HeikinAshi from '../../assets/icons/chartTypes/heikinAshi'
import LineBreak from '../../assets/icons/chartTypes/LineBreak'
import Kagi from '../../assets/icons/chartTypes/Kagi'
import { ShortcutsCategories, ShortcutType } from '../KeyboardShortcuts/Shortcuts.types'
import { ChartTypeShortcutsKeyName } from '../KeyboardShortcuts/ShortcutsKeymap'
import useShortcuts from '../../hooks/useShortcuts'
import Renko from '../../assets/icons/chartTypes/Renko'
import PandF from '../../assets/icons/chartTypes/PandF'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import { ActionType, useUndoRedoContext } from '../UndoRedoFunctonalities/UndoRedo.context'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import HlcArea from '../../assets/icons/chartTypes/hlcArea'
import { FeatureCategory, FeatureSubCategory } from '../adminUserRestrictions/Restrictions.constants'
import { Group, setFeatureRestrictionDialog } from '../../reducer/user.reducer'
import { useUser } from '../../context/UserContext'
import LineMarkers from '../../assets/icons/chartTypes/lineMarkers.tsx'
import StepLine from '../../assets/icons/plotTypes/stepLine.tsx'
import { selectFavoriteChartTypes } from '../../selectors/user.ts'
import { updateUserPreferences } from '../../services/apis/user.ts'
import StarOutlinedIcon from '../../assets/icons/StarOutlinedIcon.tsx'
import { Star } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import ThinArrowUpIcon from '../../assets/icons/ThinArrowUpIcon'


interface ChartTypeMenuProps {
    viewKey: string
    disabled?: boolean
    screenerChart?: boolean
}
export function getIcon(type: ChartType) {
    switch (type) {
        case ChartType.BAR:
            return <Bar />
        case ChartType.CANDLESTICK:
            return <Candle />
        case ChartType.AREA:
            return <Area />
        case ChartType.LINE:
            return <Line />
        case ChartType.HISTOGRAM:
            return <Histogram />
        case ChartType.HEIKIN_ASHI:
            return <HeikinAshi />
        case ChartType.LINE_BREAK:
            return <LineBreak />
        case ChartType.KAGI:
            return <Kagi />
        case ChartType.RENKO:
            return <Renko />
        case ChartType.POINT_AND_FIGURE:
            return <PandF />
        case ChartType.HOLLOW_CANDLES:
            return <HollowCandle />;
        case ChartType.HIGH_LOW:
            return <HighLowCandle />;
        case ChartType.BASELINE:
            return <Baseline />;
        case ChartType.HLC_AREA:
            return <HlcArea />;
        case ChartType.VOLUME_CANDLESTICK:
            return <Candle />;
        case ChartType.STEP_LINE_CHART:
            return <StepLine />;
        case ChartType.LINE_WITH_MARKERS:
            return <LineMarkers />;
        default:
            return null
    }
}

const ChartTypeMenu = React.memo(({ viewKey, disabled = false, screenerChart = false }: ChartTypeMenuProps) => {
    const selectedChartType = useSelector(screenerChart ? selectScreenerChartType : selectChartType(viewKey))
    const { push } = useUndoRedoContext()
    const allChartTypes = useSelector(selectChartTypes)
    
    
    const chartTypes = useMemo(() => {
        if (screenerChart) {
            const allowedTypes = [ChartType.BASELINE, ChartType.BAR, ChartType.LINE, ChartType.CANDLESTICK]
            return allChartTypes.filter(ct => allowedTypes.includes(chartTypeFromJSON(ct.code)))
        }
        return allChartTypes
    }, [screenerChart, allChartTypes])
    
    const dispatch = useDispatch()
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const { user: { hasAccess } } = useUser()

    const handleClose = () => {
        setAnchorEl(null)
    }

    const theme = useTheme()

    const shortcuts: ShortcutType = useMemo(() => ({
        [ChartTypeShortcutsKeyName.openChartTypeMenu]: () => {
            AnalyticsManager.logEvent('Chart Type Menu Opened', { source: 'keyboard' })
            setAnchorEl(buttonRef?.current)
        }
    }), [buttonRef])

    useShortcuts(ShortcutsCategories.CHART_TYPE, shortcuts)
    const container = useSelector(selectMultiPaneFullScreen)

    const favoriteChartTypes = useSelector(selectFavoriteChartTypes)

    const toggleFavorite = useCallback((chartType: SuperChartConfiguration_ChartType) => {
        const typeCode = chartType.code
        const newFavorites = [...favoriteChartTypes]
        if (newFavorites.includes(typeCode)) {
            newFavorites.splice(newFavorites.indexOf(typeCode), 1)
        } else {
            newFavorites.push(typeCode)
        }
        updateUserPreferences({
            body: {
                favoriteChartTypes: newFavorites
            }
        })
    }, [favoriteChartTypes])

    const onChartTypeMenuItemClicked = useCallback((type: ChartType) => {
        if (selectedChartType !== type) {
            if (screenerChart) {
                dispatch(updateScreenerChartType(type))
            } else {
                dispatch(updateChartType({ viewKey, chartType: type }))
            }
        }
        setAnchorEl(null)
    }, [selectedChartType, viewKey, screenerChart])

    const handleFavoriteClick = useCallback((type: ChartType) => {
        if (selectedChartType !== type) {
            onChartTypeMenuItemClicked(type)
        }
    }, [selectedChartType, onChartTypeMenuItemClicked])

    const typesDisplayList = useMemo(() => {
        if (favoriteChartTypes.length === 0) return []

        const list = [...favoriteChartTypes]
        // Check if selected is in favorites
        const isSelectedFav = list.some(code => chartTypeFromJSON(code) === selectedChartType)

        if (!isSelectedFav) {
            // Find the code for selectedChartType
            const selectedConfig = chartTypes.find(c => chartTypeFromJSON(c.code) === selectedChartType)
            if (selectedConfig) {
                list.push(selectedConfig.code)
            }
        }
        return list
    }, [favoriteChartTypes, selectedChartType, chartTypes])

    const shortcutTooltipTitle = (
        <span style={{ fontSize: '12px' }}>
            Chart Type : <Box component="kbd"
                sx={{
                    display: 'inline-block', padding: '2px 6px', fontSize: '12px',
                    backgroundColor: 'grey',
                    borderRadius: '4px',
                    margin: '0 2px'
                }}
            >
                ⌥
            </Box>
            +
            <Box
                component="kbd"
                sx={{
                    display: 'inline-block',
                    padding: '2px 6px',
                    fontSize: '12px',
                    backgroundColor: 'grey',
                    borderRadius: '4px',
                    margin: '0 2px'
                }}
            >
                T
            </Box>
        </span>
    )

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {favoriteChartTypes.length > 0 ? (
                <>
                    {typesDisplayList.map((typeCode) => {
                        const chartTypeConfig = chartTypes.find(c => c.code === typeCode)
                        if (!chartTypeConfig) return null
                        const type = chartTypeFromJSON(chartTypeConfig.code)
                        return (
                            <Tooltip key={typeCode} title={chartTypeConfig.name} arrow placement="bottom">
                                <span>
                                    <ExtraSmallButton
                                        size="small"
                                        color="inherit"
                                        onClick={() => onChartTypeMenuItemClicked(type)}
                                        sx={{
                                            backgroundColor: selectedChartType === type ? (theme) => theme.palette.action.selected : 'transparent',
                                            margin: '0 2px'
                                        }}
                                    >
                                        {getIcon(type) || <span />}
                                    </ExtraSmallButton>
                                </span>
                            </Tooltip>
                        )
                    })}
                    <Tooltip
                        title={shortcutTooltipTitle}
                        arrow
                        placement="bottom"
                    >
                        <span>
                        <ExtraSmallButton ref={buttonRef} size="small" color="inherit" onClick={handleButtonClick}
                            disabled={disabled}
                            data-event-source={viewKey === 'screener-chart-' ? 'stock-screener' : 'stock-bar'}
                            data-event={'ChartType Menu Opened'}>
                            {open ? <ThinArrowUpIcon fontSize={'small'} /> : <ThinArrowDownIcon fontSize={'small'} />}
                        </ExtraSmallButton>
                        </span>
                    </Tooltip>
                </>
            ) : (
                <Tooltip
                    title={shortcutTooltipTitle}
                    arrow
                    placement="bottom"
                >
                    <span>
                    <ExtraSmallButton ref={buttonRef} size="small" color="inherit" onClick={handleButtonClick}
                        disabled={disabled}
                        data-event-source={viewKey === 'screener-chart-' ? 'stock-screener' : 'stock-bar'}
                        data-event={'ChartType Menu Opened'}>
                        {getIcon(selectedChartType) || <span />}
                    </ExtraSmallButton>
                    </span>
                </Tooltip>
            )}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                transitionDuration={0}
            >
                {Object.entries(
                    chartTypes.reduce((acc, chartType) => {
                        const group = chartType.group || 'OTHER';
                        if (!acc[group]) acc[group] = [];
                        acc[group].push(chartType);
                        return acc;
                    }, {} as Record<string, SuperChartConfiguration_ChartType[]>)
                ).sort(([groupA], [groupB]) => {
                    const order = ['CANDLE', 'LINE', 'AREA', 'BAR', 'PRICE_BASED', 'OTHER'];
                    return order.indexOf(groupA) - order.indexOf(groupB);
                }).map(([group, types], index) => (
                    <div key={group}>
                        {index > 0 && !screenerChart && <Divider
                            sx={{
                                my: 0.5,
                                backgroundColor: theme.palette.mode === 'dark' ? '#434651' : '#e0e3eb'
                            }}
                        />}
                        {types.map((chartType) => {
                            const type = chartTypeFromJSON(chartType.code);
                            return (
                                <MenuItem
                                    selected={selectedChartType === type}
                                    key={chartType.code}
                                    onClick={() => {
                                        if (selectedChartType !== type) {
                                            if (screenerChart) {
                                                dispatch(updateScreenerChartType(type));
                                            } else {
                                                let chartTypeToHistory = true;
                                                dispatch(updateChartType({ viewKey, chartType: type }));
                                                const data = [
                                                    {
                                                        chartID: viewKey,
                                                        currentData: type,
                                                        previousData: selectedChartType
                                                    }
                                                ];
                                                if (chartTypeToHistory) {
                                                    push(data, `Chart Type (${chartType.name})`, ActionType.CHART_TYPE);
                                                }
                                            }
                                        }
                                        setAnchorEl(null);
                                    }}
                                    data-event={`Chart Candle Type Changed`}
                                    data-event-candle-type={chartType.name}
                                    data-event-source={viewKey === 'screener-chart-' ? 'stock-screener' : 'stock-bar'}
                                >
                                    <ListItemIcon>
                                        {getIcon(type)}
                                    </ListItemIcon>
                                    <ListItemText>{chartType.name}</ListItemText>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            toggleFavorite(chartType)
                                        }}
                                        sx={{
                                            padding: 0.5,
                                            marginLeft: 1,
                                            visibility: 'hidden',
                                            '&.MuiIconButton-root': {
                                                visibility: favoriteChartTypes.includes(chartType.code) ? 'visible' : 'hidden'
                                            }
                                        }}
                                        className="star-icon-button"
                                    >
                                        {favoriteChartTypes.includes(chartType.code) ? <Star fontSize="small" /> : <StarOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                </MenuItem>
                            );
                        })}
                    </div>
                )).map((groupElement: any) => {
                    return React.cloneElement(groupElement, {
                        children: React.Children.map(groupElement.props.children, (child) => {
                            if (child?.type === MenuItem) {
                                return React.cloneElement(child as React.ReactElement<any>, {
                                    sx: {
                                        ...child.props.sx,
                                        '&:hover .star-icon-button': {
                                            visibility: 'visible'
                                        }
                                    }
                                })
                            }
                            return child
                        })
                    })
                })
                }
            </Menu>
        </div>
    )
})

export default ChartTypeMenu
